import {
  generatePostAction,
  generatePutAction,
  generateGetAction,
  generateAction,
  generateDeleteAction,
} from '@services/ActionDispatcher';

import {
  createPatientInformationAction,
  updatePatientInformationAction,
  getReferralsAction,
  getClinicReferralsAction,
  showReferralAction,
  showClinicReferralAction,
  createPatientHistoryAction,
  updatePatientHistoryAction,
  getReferredByListAction,
  getDentistListAction,
  getClinicListAction,
  saveReferralInfoAction,
  saveReferralReasonAction,
  saveReferralAttachmentAction,
  getReferralMessagesAction,
  sendReferralMessageAction,
  markReferralConversationReadAction,
  markReferralConversationInListReadAction,
  markReferralConversationInListUnreadAction,
  updateReferralStatusAction,
  getUnreadReferralConversationAction,
  deleteReferralAction,
  copyPatientRecordAction,
  deleteReferralMessageAttachmentAction,
  deleteReferralAttachmentAction,
  getReferralMessageThreadAction,
  getReferralMessageAttachmentsAction,
  downloadReferralMessageAttachmentAction,
  getLaboratoryListAction,
  getAffiliatedClinicsAction,
  saveReferralLabInfoAction,
  getLabOrderCaseTypesAction,
  getLabOrderMaterialTypesAction,
  getLabOrderImpressionTypesAction,
  saveReferralLabOrderDetailsAction,
  saveReferralLabOrderAttachmentsAction,
  showLabOrderAction,
  deleteLabOrderAction,

  clearReferralDataAction,
  displayMessageAction,
  updateMessageAction,
  deleteMessageAction,
  updateUnreadReferralMessageCountAction,
  referralMessageArrivedAction,
  updateReferralListAction,
  updateReferralMessageStatusAction,
  deleteReceiverMessageAction,
  markAsUrgentAction,
  getAffiliatedReferralsAction,
  generateSummaryFileRequestAction,
  updateReferralInfoAction,
  updateReferralLabInfoAction,
  updateReferralFollowUpStatusAction,
  updateReferralFilterAction,
  updateReferralReferredByAction,
  updateReferralReferredToAction,
  getHomeOngoingReferralsAction,
} from './ReferralActionConfig';

const clearReferralDataDispatch = () => generateAction(
  clearReferralDataAction,
);

const displayMessageDispatch = (data: any) => generateAction(displayMessageAction, data);
const updateMessageDispatch = (data: any) => generateAction(updateMessageAction, data);
const deleteMessageDispatch = (data: any) => generateAction(deleteMessageAction, data);
const updateUnreadReferralMessageCountDispatch = (
  data: any) => generateAction(updateUnreadReferralMessageCountAction, data);
const referralMessageArrivedDispatch = () => generateAction(referralMessageArrivedAction);
const updateReferralListDispatch = (data: any) => generateAction(updateReferralListAction, data);
const updateReferralMessageStatusDispatch = (
  data: any) => generateAction(updateReferralMessageStatusAction, data);
const deleteReceiverMessageDispatch = (
  data: any) => generateAction(deleteReceiverMessageAction, data);
const markAsUrgentDispatch = (data: any) => generateAction(markAsUrgentAction, data);

const getReferralsRequest = (params: any) => generateGetAction(
  // action name
  getReferralsAction,
  // url
  'referrals',
  {
    queryParams: params,
  },
  params,
);

const updateReferralFilterActionRequest = (data: any) => generateAction(
  updateReferralFilterAction,
  data,
);

const updateReferralReferredByActionRequest = (data: [string, string][]) => generateAction(
  updateReferralReferredByAction,
  data,
);

const updateReferralReferredToActionRequest = (data: [string, string][]) => generateAction(
  updateReferralReferredToAction,
  data,
);

const showReferralRequest = (id: any) => generateGetAction(
  // action name
  showReferralAction,
  // url
  `referrals/${id}`,
);

const getClinicReferralsRequest = (params: any) => generateGetAction(
  // action name
  getClinicReferralsAction,
  // url
  'clinic/referrals',
  {
    queryParams: params,
  },
  params,
);

/**
 * Home's "Ongoing referrals" table keeps its rows to itself. On the shared list
 * action, a late response from it replaced the list the Referrals page had just
 * loaded, so opening the page from a Home widget could show Home's ongoing rows.
 */
const getHomeOngoingReferralsRequest = (params: any, isClinic: boolean) => generateGetAction(
  // action name
  getHomeOngoingReferralsAction,
  // url
  isClinic ? 'clinic/referrals' : 'referrals',
  {
    queryParams: params,
  },
  params,
);

const getAffiliatedReferralsRequest = (params: any) => generateGetAction(
  // action name
  getAffiliatedReferralsAction,
  // url
  'clinic/referrals/affiliated',
  {
    queryParams: params,
  },
  params,
);

const showClinicReferralRequest = (id: any) => generateGetAction(
  // action name
  showClinicReferralAction,
  // url
  `clinic/referrals/${id}`,
);

const createPatientInformationRequest = (data: any) => generatePostAction(
  // action name
  createPatientInformationAction,
  // url
  'referrals/patient/save',
  data,
);

const updatePatientInformationRequest = (data: any, patientId: any) => generatePutAction(
  // action name
  updatePatientInformationAction,
  // url
  `referrals/patient/${patientId}/update`,
  data,
);

const createPatientHistoryRequest = (data: any) => generatePostAction(
  // action name
  createPatientHistoryAction,
  // url
  'referrals/patient/history/save',
  data,
);

const updatePatientHistoryRequest = (data: any, id: any) => generatePutAction(
  // action name
  updatePatientHistoryAction,
  // url
  `referrals/patient/history/${id}/update`,
  data,
);

const getReferredByListRequest = (params: any) => generateGetAction(
  // action name
  getReferredByListAction,
  // url
  'referredBy',
  {
    queryParams: params,
  },
);

const getDentistListRequest = (params: any) => generateGetAction(
  // action name
  getDentistListAction,
  // url
  'referrals/dentist/list',
  {
    queryParams: params,
  },
);

const getClinicListRequest = (params: any) => generateGetAction(
  // action name
  getClinicListAction,
  // url
  'referrals/clinic/list',
  {
    queryParams: params,
  },
);

const saveReferralInfoRequest = (data: any) => generatePostAction(
  // action name
  saveReferralInfoAction,
  // url
  'referrals/info/save',
  data,
);

const updateReferralInfoRequest = (data: any) => generatePutAction(
  // action name
  updateReferralInfoAction,
  // url
  'referrals/info/update',
  data,
);

const saveReferralReasonRequest = (data: any) => generatePostAction(
  // action name
  saveReferralReasonAction,
  // url
  'referrals/reason/save',
  data,
);

const saveReferralAttachmentRequest = (data: any) => generatePostAction(
  // action name
  saveReferralAttachmentAction,
  // url
  'referrals/attachments/save',
  data,
);

const getReferralMessagesRequest = (id: any, params: any) => generateGetAction(
  // action name
  getReferralMessagesAction,
  // url
  `referrals/${id}/messages`,
  {
    queryParams: params,
  },
  { id },
);

const sendReferralMessageRequest = (id: any, data: any) => generatePostAction(
  // action name
  sendReferralMessageAction,
  // url
  `referrals/${id}/messages/send`,
  data,
);

const markReferralConversationReadRequest = (id: any, userId: any) => generatePostAction(
  // action name
  markReferralConversationReadAction,
  // url
  `referrals/${id}/messages/mark-as-read`,
  { userId },
);

// this is used to mark referrals as read via the action button
const markReferralConversationInListReadRequest = (data: any) => generatePostAction(
  // action name
  markReferralConversationInListReadAction,
  // url
  `referrals/${data?.referral_id}/messages/mark-as-read`,
  { data },
);

// this is used to mark referrals as unread via the action button
const markReferralConversationInListUnreadRequest = (data: any) => generatePostAction(
  // action name
  markReferralConversationInListUnreadAction,
  // url
  `referrals/${data?.referral_id}/messages/mark-as-unread`,
  { data },
);

const updateReferralStatusRequest = (data: any, id: any) => generatePutAction(
  // action name
  updateReferralStatusAction,
  // url
  `referrals/${id}/status`,
  data,
);

const getUnreadReferralConversationRequest = () => generateGetAction(
  // action name
  getUnreadReferralConversationAction,
  // url
  'referrals/messages/unread',
);

const deleteReferralRequest = (id: any) => generateDeleteAction(
  // action name
  deleteReferralAction,
  // url
  `referrals/${id}`,
);

const copyPatientRecordRequest = (id: any, type: string) => generatePostAction(
  // action name
  copyPatientRecordAction,
  // url
  `referrals/patient/${id}/copy`,
  { type },
);

const deleteReferralMessageAttachmentRequest = (data: any) => generateDeleteAction(
  // action name
  deleteReferralMessageAttachmentAction,
  // url
  `referrals/messages/${data?.id}/attachment/${data?.attachment_id}`,
  { data },
);

const deleteReferralAttachmentRequest = (data: any) => generateDeleteAction(
  // action name
  deleteReferralAttachmentAction,
  // url
  `referrals/attachment/${data?.id}`,
  { data },
);

const getReferralMessageThreadRequest = (id: any) => generateGetAction(
  // action name
  getReferralMessageThreadAction,
  // url
  `referrals/${id}/messages/thread`,
);

const getReferralMessageAttachmentsRequest = (id: any, params: any) => generateGetAction(
  // action name
  getReferralMessageAttachmentsAction,
  // url
  `referrals/${id}/messages/attachments`,
  {
    queryParams: params,
  },
  params,
);

const downloadReferralMessageAttachmentRequest = (data: any) => generatePostAction(
  // action name
  downloadReferralMessageAttachmentAction,
  // url
  `referrals/${data?.id}/messages/attachments/${data?.attachment_id}/download`,
  data,
);

const generateSummaryFileRequest = (data: any) => generatePostAction(
  // action name
  generateSummaryFileRequestAction,
  // url
  `referrals/${data?.id}/generate-summary`,
  data,
);

const getLaboratoryListRequest = (params: any) => generateGetAction(
  // action name
  getLaboratoryListAction,
  // url
  'lab-orders/lab-information/laboratories',
  {
    queryParams: params,
  },
);

const getAffiliatedClinicsRequest = (params: any) => generateGetAction(
  // action name
  getAffiliatedClinicsAction,
  // url
  'affiliated-clinics',
  {
    queryParams: params,
  },
);

const saveReferralLabInfoRequest = (data: any) => generatePostAction(
  // action name
  saveReferralLabInfoAction,
  // url
  'lab-orders/lab-information/save',
  data,
);

const updateReferralLabInfoRequest = (data: any) => generatePutAction(
  // action name
  updateReferralLabInfoAction,
  // url
  'lab-orders/lab-information/update',
  data,
);

const getLabOrderCaseTypesRequest = (params: any) => generateGetAction(
  // action name
  getLabOrderCaseTypesAction,
  // url
  'lab-orders/details/case-types',
  {
    queryParams: params,
  },
);

const getLabOrderMaterialTypesRequest = (params: any) => generateGetAction(
  // action name
  getLabOrderMaterialTypesAction,
  // url
  'lab-orders/details/material-types',
  {
    queryParams: params,
  },
);

const getLabOrderImpressionTypesRequest = (params: any) => generateGetAction(
  // action name
  getLabOrderImpressionTypesAction,
  // url
  'lab-orders/details/impression-types',
  {
    queryParams: params,
  },
);

const saveReferralLabOrderDetailsRequest = (data: any) => generatePostAction(
  // action name
  saveReferralLabOrderDetailsAction,
  // url
  'lab-orders/details/save',
  data,
);

const saveReferralLabOrderAttachmentsRequest = (data: any) => generatePostAction(
  // action name
  saveReferralLabOrderAttachmentsAction,
  // url
  'lab-orders/attachments/save',
  data,
);

const showLabOrderRequest = (id: any) => generateGetAction(
  // action name
  showLabOrderAction,
  // url
  `lab-orders/${id}`,
);

const deleteLabOrderRequest = (id: any) => generateDeleteAction(
  // action name
  deleteLabOrderAction,
  // url
  `lab-orders/${id}`,
);

const updateReferralFollowUpStatusRequest = (id: any, data: any) => generatePutAction(
  // action name
  updateReferralFollowUpStatusAction,
  // url
  `referrals/${id}/follow-up`,
  data,
);

export {
  // normal dispatch
  clearReferralDataDispatch,
  displayMessageDispatch,
  updateMessageDispatch,
  deleteMessageDispatch,
  updateUnreadReferralMessageCountDispatch,
  referralMessageArrivedDispatch,
  updateReferralListDispatch,
  updateReferralMessageStatusDispatch,
  deleteReceiverMessageDispatch,
  markAsUrgentDispatch,

  // async (request) dispatch
  createPatientInformationRequest,
  updatePatientInformationRequest,
  getReferralsRequest,
  showReferralRequest,
  getClinicReferralsRequest,
  getHomeOngoingReferralsRequest,
  showClinicReferralRequest,
  createPatientHistoryRequest,
  updatePatientHistoryRequest,
  getReferredByListRequest,
  getDentistListRequest,
  getClinicListRequest,
  saveReferralInfoRequest,
  saveReferralReasonRequest,
  saveReferralAttachmentRequest,
  getReferralMessagesRequest,
  sendReferralMessageRequest,
  markReferralConversationReadRequest,
  markReferralConversationInListReadRequest,
  markReferralConversationInListUnreadRequest,
  updateReferralStatusRequest,
  getUnreadReferralConversationRequest,
  deleteReferralRequest,
  copyPatientRecordRequest,
  deleteReferralMessageAttachmentRequest,
  deleteReferralAttachmentRequest,
  getReferralMessageThreadRequest,
  getReferralMessageAttachmentsRequest,
  downloadReferralMessageAttachmentRequest,
  getLaboratoryListRequest,
  getAffiliatedClinicsRequest,
  saveReferralLabInfoRequest,
  getLabOrderCaseTypesRequest,
  getLabOrderMaterialTypesRequest,
  getLabOrderImpressionTypesRequest,
  saveReferralLabOrderDetailsRequest,
  saveReferralLabOrderAttachmentsRequest,
  showLabOrderRequest,
  deleteLabOrderRequest,
  getAffiliatedReferralsRequest,
  generateSummaryFileRequest,
  updateReferralInfoRequest,
  updateReferralLabInfoRequest,
  updateReferralFollowUpStatusRequest,
  updateReferralFilterActionRequest,
  updateReferralReferredByActionRequest,
  updateReferralReferredToActionRequest,
};
